import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import { fetchJson } from "../../helpers/fetch.js";

import { project_get_form_answers_path } from '~/routes';

// Helper to get state key for answer
const getKeyFromObj = (obj) => {
  const { projectId, formId, formEntity = "ProjectCallPhaseContent", reportKey } = obj || {};
  return `${projectId}.${formId}.${formEntity}.${reportKey}`;
};

// Internal selectors, which are not exported and receive custom args instead of state/props
const internals = {
  // combiner func of makeVariablesLoadingSelector
  variablesLoading: (loading, variableKeys) => {
    let isLoading = false;

    variableKeys.forEach((k) => {
      if (loading.includes(k)) {
        isLoading = true;
        return;
      }
    });

    return isLoading;
  },
  // combiner func of makeVariableValuesSelector
  variableValues: (answers, variables) => {
    let values = {};
    variables.forEach(({ name, key }) => (values[name] = { ...answers[key] }));
    return values;
  },
};

export const selectors = {
  // Selector to get the state answers.
  answers: ({ formAnswer }) => formAnswer.answers,
  queue: ({ formAnswer: { queue } }, { projectId }) => queue[projectId] || [],
  fetching: ({ formAnswer: { fetching } }, { projectId }) => fetching[projectId] || [],
  getVariableKeys: (_, { projectId, variables }) => variables.map((v) => getKeyFromObj({ projectId, ...v })),
  variablesWithKey: (_, { projectId, variables }) =>
    variables.map((v) => ({
      name: v.name,
      key: getKeyFromObj({ projectId, ...v }),
    })),
  // Selector to get loading variable values.
  loading: (state, props) => [...selectors.queue(state, props), ...selectors.fetching(state, props)],
  // Returns selector instance
  makeVariablesLoadingSelector: () => createSelector(selectors.loading, selectors.getVariableKeys, internals.variablesLoading),

  // Returns selector instance
  makeVariableValuesSelector: () => createSelector(selectors.answers, selectors.variablesWithKey, internals.variableValues),
};

// Thunk for fetching answers from queue
const fetchQueuedAnswers = createAsyncThunk(
  "formAnswer/fetchQueued",
  (projectId, { getState }) => {
    const { fetching, variables } = getState().formAnswer;

    return fetchJson(
      project_get_form_answers_path({
        project_id: projectId,
      }),
      {
        method: "POST",
        body: JSON.stringify({
          variables: fetching[projectId].map((key) => variables[key]),
        }),
      }
    );
  },
  {
    condition: (projectId, { getState }) => {
      const { queue } = getState().formAnswer;

      if ((queue[projectId] || []).length == 0) {
        // Don't fetch if queue is empty
        return false;
      }
    },
  }
);

// Slice sets up reducers and actions
const formAnswerSlice = createSlice({
  name: "formAnswer",
  initialState: {
    queue: {},
    fetching: {},
    variables: {},
    answers: {},
  },
  reducers: {
    addToQueue(state, action) {
      const key = getKeyFromObj(action.payload);
      const { formId, formEntity, reportKey, projectId } = action.payload;
      state.queue[projectId] = state.queue[projectId] || [];

      // No need to add to queue if queued or fetching
      if (!(state.queue[projectId].includes(key) || (state.fetching[projectId] || []).includes(key))) {
        state.queue[projectId].push(key);
        state.variables[key] = {
          form_id: formId,
          form_entity: formEntity,
          report_key: reportKey,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Start fetching, move keys from queue to fetching
      .addCase(fetchQueuedAnswers.pending, (state, action) => {
        const projectId = action.meta.arg;
        state.fetching[projectId] = [...state.queue[projectId]];
        state.queue[projectId] = [];
      })
      // Fetched, add fetched values to answers
      .addCase(fetchQueuedAnswers.fulfilled, (state, action) => {
        const projectId = action.meta.arg;
        state.fetching[projectId] = [];

        (action.payload || []).forEach(({ form_id, form_entity, report_key, data_type, value }) => {
          const key = getKeyFromObj({ projectId, formId: form_id, formEntity: form_entity, reportKey: report_key });
          state.answers[key] = { data_type, value };
        });
      });
  },
});

const { reducer, actions } = formAnswerSlice;

export const { addToQueue } = actions;
export { reducer, fetchQueuedAnswers };
